import { formatTokenCount } from "./previewMisc";
import { extractVariables } from "./processVariables";
import {
  extractBlockNames,
  replacePlainTextFragments,
  replaceRichTextFragments,
  generateFragmentReport,
} from "./processFragments";
import {
  getAllPromptNames,
  extractPromptInfoFromProperties,
} from "./processPrompts";
import { getRequiredVariables } from "../../services/promptService";
import memoize from "lodash/memoize";

const SECTIONS = {
  PREPROCESSING: "preprocessing",
  PRIMARY: "primary",
  POSTPROCESSING: "postprocessing",
  VOICEMAIL: "voicemail",
};

const createMemoizedTokenCounter = (calculateTokenCount) =>
  memoize(calculateTokenCount);

const processTextVersions = (fileContent, fragmentMap) => {
  const plainTextVersion = replacePlainTextFragments(fileContent, fragmentMap);
  const richTextVersion = replaceRichTextFragments(fileContent, fragmentMap);
  return { plainTextVersion, richTextVersion };
};

const processMetadata = (fileContent, fragmentMap) => {
  const blockNamesInContent = extractBlockNames(fileContent);
  const fragmentReport = generateFragmentReport(
    blockNamesInContent,
    fragmentMap
  );

  return { blockNamesInContent, fragmentReport };
};

export const processAllFiles = async (
  promptPaths,
  { contents, calculateTokenCount },
  fragmentMap,
  topicKey,
  version
) => {
  const memoizedTokenCounter = createMemoizedTokenCounter(calculateTokenCount);

  const processSectionFiles = async (paths) => {
    try {
      return await Promise.all(
        paths.map((path) =>
          processFileContent({
            key: topicKey,
            version: version,
            filePath: path,
            fileContent: contents[path],
            fragmentMap,
            calculateTokenCount: memoizedTokenCounter,
          }).catch((error) => {
            console.error(`Error processing file ${path}:`, error);
            return {
              path,
              error: `Failed to process: ${error.message}`,
              plainText: "",
              richText: "",
              blockNames: [],
              variables: [],
              fragmentReport: {},
              variableReport: "",
              tokenCount: 0,
              formattedTokenCount: "0",
              configuredVariables: [],
              unconfiguredVariables: [],
            };
          })
        )
      );
    } catch (error) {
      console.error("Error processing section files:", error);
      return [];
    }
  };

  try {
    return {
      [SECTIONS.PREPROCESSING]: await processSectionFiles(
        promptPaths.preprocessing
      ),
      [SECTIONS.PRIMARY]: await processSectionFiles(promptPaths.primary),
      [SECTIONS.POSTPROCESSING]: await processSectionFiles(
        promptPaths.postprocessing
      ),
      [SECTIONS.VOICEMAIL]: await processSectionFiles(promptPaths.voicemail),
    };
  } catch (error) {
    console.error("Failed to process all files:", error);
    return {
      [SECTIONS.PREPROCESSING]: [],
      [SECTIONS.PRIMARY]: [],
      [SECTIONS.POSTPROCESSING]: [],
      [SECTIONS.VOICEMAIL]: [],
      error: error.message,
    };
  }
};

export const processFileContent = async ({
  key,
  version,
  filePath,
  fileContent,
  fragmentMap,
  calculateTokenCount,
}) => {
  try {
    const { blockNamesInContent, fragmentReport } = processMetadata(
      fileContent,
      fragmentMap
    );
    const variablesInContent = await getRequiredVariables({
      topicKey: key,
      version: version,
      promptName: filePath.split("/").pop(),
      fragments: blockNamesInContent,
    });

    const { plainTextVersion, richTextVersion } = processTextVersions(
      fileContent,
      fragmentMap
    );

    const tokenCount = await calculateTokenCount(plainTextVersion);
    const formattedTokenCount = formatTokenCount(tokenCount);

    return {
      path: filePath,
      plainText: plainTextVersion,
      richText: richTextVersion,
      blockNames: blockNamesInContent,
      variables: variablesInContent,
      fragmentReport,
      variableReport: "",
      tokenCount,
      formattedTokenCount,
      configuredVariables: [],
      unconfiguredVariables: [],
    };
  } catch (error) {
    console.error(`Error in processFileContent for ${filePath}:`, error);
    throw error;
  }
};

export const enhanceFilesWithPromptInfo = (
  processedFiles,
  promptProperties = {}
) => {
  try {
    const promptInfo = promptProperties
      ? extractPromptInfoFromProperties(promptProperties, processedFiles)
      : {};

    [
      SECTIONS.PREPROCESSING,
      SECTIONS.PRIMARY,
      SECTIONS.POSTPROCESSING,
      SECTIONS.VOICEMAIL,
    ].forEach((section) => {
      if (!processedFiles[section]) {
        console.warn(`Section ${section} not found in processed files`);
        return;
      }

      processedFiles[section].forEach((file) => {
        if (!file || !file.path) {
          console.warn(`Invalid file object found in ${section}`);
          return;
        }

        const filename = file.path.split("/").pop();
        file.promptInfo = promptInfo[filename] || {};
      });
    });

    return { allPromptNames: getAllPromptNames(processedFiles) };
  } catch (error) {
    console.error("Error enhancing files with prompt info:", error);
    return { allPromptNames: [] };
  }
};
